import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({ author: '', quote: '' });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTestimonials = async () => {
    const snap = await getDocs(
      query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'))
    );
    setTestimonials(snap.docs.map(docItem => ({ id: docItem.id, ...docItem.data() })));
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.author.trim() || !form.quote.trim()) return;

    setLoading(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'testimonials', editingId), {
          author: form.author,
          quote: form.quote,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'testimonials'), {
          author: form.author,
          quote: form.quote,
          createdAt: serverTimestamp()
        });
      }
      setForm({ author: '', quote: '' });
      setEditingId(null);
      await fetchTestimonials();
    } finally {
      setLoading(false);
    }
  };

  const startEdit = item => {
    setEditingId(item.id);
    setForm({ author: item.author || '', quote: item.quote || '' });
  };

  const removeItem = async id => {
    if (!window.confirm('Hapus testimoni ini?')) return;
    await deleteDoc(doc(db, 'testimonials', id));
    await fetchTestimonials();
  };

  const isAll = pageSize === 'all';
  const totalItems = testimonials.length;
  const totalPages = isAll ? 1 : Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = isAll ? 0 : (currentPage - 1) * pageSize;
  const endIndex = isAll ? totalItems : startIndex + pageSize;
  const displayed = isAll ? testimonials : testimonials.slice(startIndex, endIndex);

  return (
    <div className="admin-page admin-testimonial-page">
      <div className="admin-page-header">
        <div>
          <h2>Testimoni</h2>
          <p className="text-muted-small">
            Kelola testimoni yang tampil di halaman utama.
          </p>
        </div>
      </div>

      <div className="admin-card">
        <h3>{editingId ? 'Edit Testimoni' : 'Tambah Testimoni Baru'}</h3>
        <form className="testimonial-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Nama"
            value={form.author}
            onChange={e => setForm(prev => ({ ...prev, author: e.target.value }))}
            required
          />
          <textarea
            rows="3"
            placeholder="Isi testimoni"
            value={form.quote}
            onChange={e => setForm(prev => ({ ...prev, quote: e.target.value }))}
            required
          />
          <div className="admin-form-actions">
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {editingId ? 'Simpan Perubahan' : 'Tambah Testimoni'}
            </button>
            {editingId && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setEditingId(null);
                  setForm({ author: '', quote: '' });
                }}
              >
                Batal
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h3>Daftar Testimoni</h3>
        <div className="admin-toolbar admin-toolbar-inline">
          <div className="admin-pagination-info">
            Menampilkan {totalItems === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, totalItems)} dari {totalItems} testimoni
          </div>
          <div className="admin-toolbar-group">
            <label className="text-muted-small">Tampil</label>
            <select
              className="form-select w-auto"
              value={pageSize}
              onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">Semua</option>
            </select>
          </div>
        </div>
        {testimonials.length === 0 ? (
          <p className="text-muted-small">Belum ada testimoni.</p>
        ) : (
          <div className="table-responsive admin-table-wrapper">
            <table className="table admin-table align-middle admin-testimonial-table">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Nama</th>
                  <th>Isi</th>
                  <th style={{ width: 120 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map(item => (
                  <tr key={item.id}>
                    <td className="admin-id" title={item.id}>
                      {item.id.slice(0, 8)}
                    </td>
                    <td>{item.author}</td>
                    <td className="admin-quote">{item.quote}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => startEdit(item)}
                          aria-label="Edit testimoni"
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => removeItem(item.id)}
                          aria-label="Hapus testimoni"
                          title="Hapus"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isAll && totalPages > 1 && (
          <div className="pagination-footer admin-pagination-footer">
            <button
              className="btn btn-outline-success btn-sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              ← Sebelumnya
            </button>
            <select
              className="form-select form-select-sm page-select"
              value={currentPage}
              onChange={e => setCurrentPage(Number(e.target.value))}
            >
              {Array.from({ length: totalPages }).map((_, idx) => (
                <option key={idx + 1} value={idx + 1}>
                  Halaman {idx + 1} / {totalPages}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-success btn-sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Berikutnya →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
